import React, { useState, useRef, useEffect } from "react";
import "./addassets.scss";

import BreadCrumsHeader from "../../common/BreadCrums";
import Input from "../../common/FormInputs";
import FormLayout from "../../common/FormLayout";

import { useFormik } from "formik";
import { assetsSchema } from "../../../utils/validationSchemas";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createAsset, editAsset } from "../../../services/assetsService";
import InputTextArea from "../../common/InputTextArea";
import Loader from "../../common/Loader";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import imgIcon from "../../../Assets/Icons/imgIcon.svg";
import Selector from "../../common/Selector";
import { getFundraisers } from "../../../services/fundraiserService";
import UploadExcel from "../../common/OfferForm/Location/UploadExcel";
import toast from "../../../utils/toast";
import { uploadFile, getS3PublicUrl, config as s3Config } from "../../../utils/s3";

const AddAssetsForm = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const loading = useSelector((state) => state?.assetsReducer?.loading);
  const allAssets = useSelector((state) => state.assetsReducer.allPromotions);
  const { id = null } = location?.state || "";
  const navigate = useNavigate();
  const [fileData, setFileData] = useState();
  const [fundraisers, setFundraisers] = useState([]);
  const [fundraiserIds, setFundraiserIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({
    postcards: null,
    a1Posters: null,
    a2Posters: null,
    a3Posters: null,
    a4Posters: null,
    socialMediaBanners: null,
    digitalFlyer: null,
    qrCodes: null,
    emailTemplates: null,
    pullUpBanners: null,
    emailSignatures: null,
  });
  const [uploading, setUploading] = useState({
    postcards: false,
    a1Posters: false,
    a2Posters: false,
    a3Posters: false,
    a4Posters: false,
    socialMediaBanners: false,
    digitalFlyer: false,
    qrCodes: false,
    emailTemplates: false,
    pullUpBanners: false,
    emailSignatures: false,
  });
  const fileInputRef = useRef(null);

  const getFundarisersData = async () => {
    try {
      // Fetch only approved fundraisers with a high limit to get all
      const result = await dispatch(getFundraisers({
        status: 'approved',
        limit: 1000 // High limit to ensure we get all approved fundraisers
      })).unwrap();
      setFundraisers(result?.data);
    } catch (error) {
      console.error("Error fetching fundraisers", error);
      throw error;
    }
  };

  useEffect(() => {
    getFundarisersData();
  }, []);

  const initialValues = {
    title: "",
    assetDescription: "",
    selectedFundraisers: [],
    document: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: assetsSchema,

    onSubmit: async (values) => {
      console.log("Offer values: ", values);
      if (id !== "" && id !== null) {
        const entries = Object.entries(uploadedFiles).filter(([, v]) => Boolean(v));
        const firstEntry = entries.length > 0 ? entries[0] : null;

        // Map frontend categories to backend field names
        const fieldMap = {
          postcards: 'postcardStandard',
          a1Posters: 'a1Poster',
          a2Posters: 'a2Poster',
          a3Posters: 'a3Poster',
          a4Posters: 'a4Poster',
          socialMediaBanners: 'socialMediaBanner',
          digitalFlyer: 'eFlyer',
          qrCodes: 'qrCode',
          emailTemplates: 'emailTemplate',
          pullUpBanners: 'pullUpBanner',
          emailSignatures: 'emailSignature',
        };

        const payload = {
          id: id,
          title: values?.title,
          description: values?.assetDescription,
          fundraisers: fundraiserIds,
        };

        // Add all uploaded files to the payload
        Object.entries(uploadedFiles).forEach(([category, v]) => {
          if (v && v.url) {
            const backendField = fieldMap[category] || category;
            payload[backendField] = v.url;
            // Primary file for legacy support/fundraiser view
            if (!payload.file) {
              payload.file = v.url;
              payload.category = category;
            }
          }
        });

        // Fallback for fileUrl if no new uploads in this edit session
        if (!payload.file && fileUrl) {
          payload.file = getS3PublicUrl(fileUrl);
        }

        const editAssetPromise = await dispatch(editAsset(payload))?.unwrap();
        if (editAssetPromise?.success === true) {
          navigate("/admin/promotions-list");
        }
      } else {
        // Build entries for all uploaded files
        const entries = Object.entries(uploadedFiles).filter(([, v]) => Boolean(v));

        if (entries.length === 0 && !fileUrl) {
          toast.error('Please provide at least one asset file');
          return;
        }

        // Map frontend categories to backend field names
        const fieldMap = {
          postcards: 'postcardStandard',
          a1Posters: 'a1Poster',
          a2Posters: 'a2Poster',
          a3Posters: 'a3Poster',
          a4Posters: 'a4Poster',
          socialMediaBanners: 'socialMediaBanner',
          digitalFlyer: 'eFlyer',
          qrCodes: 'qrCode',
          emailTemplates: 'emailTemplate',
          pullUpBanners: 'pullUpBanner',
          emailSignatures: 'emailSignature',
        };

        // Create a single asset record with all uploaded files
        const payload = {
          title: values?.title,
          description: values?.assetDescription,
          fundraisers: fundraiserIds,
          fundraiser: fundraiserIds, // Keep both for safety
        };

        entries.forEach(([category, v]) => {
          const backendField = fieldMap[category] || category;
          payload[backendField] = v.url;
        });

        // Set the primary 'file' field to the first uploaded asset for legacy support
        if (entries.length > 0) {
          payload.file = entries[0][1].url;
          payload.category = entries[0][0];
        }

        const result = await dispatch(createAsset(payload)).unwrap();
        if (result?.success === true) {
          toast.success("Promotion created successfully!");
          navigate("/admin/promotions-list");
        }
      }
    },
  });

  const {
    title,
    assetDescription,
    selectedFundraisers: rawSelectedFundraisers,
    // Avoid shadowing the global `document` object
    document: assetDocument,
  } = formik.values;

  // Ensure selectedFundraisers is always an array
  const selectedFundraisers = Array.isArray(rawSelectedFundraisers) ? rawSelectedFundraisers : [];

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    setValues,
  } = formik;

  const items = [
    {
      title: "Marketing Assets",
    },
    {
      title: "Add Marketing Assets",
    },
  ];

  useEffect(() => {
    if (id !== "" && id !== null && allAssets !== null) {
      allAssets?.forEach((asset) => {
        if (asset?._id === id) {
          const selectIds = asset?.fundraisers?.map((f) => f?._id) || [];
          setFundraiserIds(selectIds);
          setValues({
            title: asset?.title,
            assetDescription: asset?.description,
            document: asset?.file,
            selectedFundraisers: asset?.fundraisers?.map((f) => f?.user?.name) || [],
          });
          setFileUrl(asset?.file !== undefined ? asset?.file : "");

          // Populate uploadedFiles state from existing asset data
          const fieldMap = {
            postcardStandard: 'postcards',
            a1Poster: 'a1Posters',
            a2Poster: 'a2Posters',
            a3Poster: 'a3Posters',
            a4Poster: 'a4Posters',
            socialMediaBanner: 'socialMediaBanners',
            eFlyer: 'digitalFlyer',
            qrCode: 'qrCodes',
            emailTemplate: 'emailTemplates',
            pullUpBanner: 'pullUpBanners',
            emailSignature: 'emailSignatures',
          };

          const newUploadedFiles = { ...uploadedFiles };
          Object.entries(fieldMap).forEach(([backendField, frontendKey]) => {
            if (asset[backendField]) {
              newUploadedFiles[frontendKey] = {
                url: getS3PublicUrl(asset[backendField]),
                name: asset[backendField].split('/').pop() || 'Existing File'
              };
            }
          });
          setUploadedFiles(newUploadedFiles);
        }
      });
    }
  }, [id, allAssets]);

  const handleFundraisersChange = (values) => {
    const fundraisersArray = Array.isArray(fundraisers) ? fundraisers : [];
    let updatedFundraisers = Array.isArray(values) ? values : [];

    // Handle Select All
    if (updatedFundraisers.includes("Select All")) {
      updatedFundraisers = fundraisersArray
        .map((f) => f?.user?.name)
        .filter(Boolean);
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }

    const updatedIds = fundraisersArray
      .filter((f) => updatedFundraisers.includes(f?.user?.name))
      .map((f) => f?._id)
      .filter(Boolean);

    setFieldValue("selectedFundraisers", updatedFundraisers);
    setFundraiserIds(updatedIds);
  };

  const handleMerchantDeselect = (removedFundraiser) => {
    const updatedFundraisers = selectedFundraisers.filter(
      (fundraiser) => fundraiser !== removedFundraiser
    );

    const fundraisersArray = Array.isArray(fundraisers) ? fundraisers : [];
    const updatedIds = fundraisersArray
      .filter((f) => updatedFundraisers.includes(f?.user?.name))
      .map((f) => f._id);

    setFieldValue("selectedFundraisers", updatedFundraisers);
    setFundraiserIds(updatedIds);
    setSelectAll(false);
  };

  // Check if form is valid to enable Save button
  const isFormValid = () => {
    const hasTitle = title && title.trim() !== "";
    const hasDescription = assetDescription && assetDescription.trim() !== "";
    const hasFundraisers = fundraiserIds && fundraiserIds.length > 0;
    const hasAtLeastOneFile = Object.values(uploadedFiles).some(file => file !== null) || fileUrl !== null;

    return hasTitle && hasDescription && hasFundraisers && hasAtLeastOneFile;
  };

  const uniqueNames = new Set();

  const fundraiserOptions = [
    { value: "Select All", label: "Select All" },
    ...(Array.isArray(fundraisers) ? fundraisers
      .map((op) => {
        const name = op?.user?.name ?? "abc";
        if (!uniqueNames.has(name)) {
          uniqueNames.add(name);
          return {
            value: name,
            label: name,
          };
        }
        return null;
      })
      .filter((option) => option !== null) : []),
  ];

  const validateFile = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (extFile == "zip" || extFile == "rar") {
      return true;
    } else {
      return false;
    }
  };

  const validateImageFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    return validTypes.includes(file.type);
  };

  const validateQrFile = (file) => {
    const validTypes = ['image/png', 'image/svg+xml'];
    return validTypes.includes(file.type);
  };

  const validateEmailTemplateFile = (file) => {
    const name = file?.name?.toLowerCase() || '';
    const ext = name.split('.').pop();
    const validExts = ['html', 'htm', 'zip'];
    const validTypes = ['text/html', 'application/zip', 'application/x-zip-compressed'];
    return validExts.includes(ext) || validTypes.includes(file.type);
  };

  const handleUpload = async (fileType, file) => {
    // Validate according to file type
    if (fileType === 'emailTemplates') {
      if (!validateEmailTemplateFile(file)) {
        toast.error('Please upload HTML or ZIP file for Email Templates');
        return;
      }
    } else if (fileType === 'qrCodes') {
      if (!validateQrFile(file)) {
        toast.error('Please upload a valid QR image (PNG or SVG only)');
        return;
      }
    } else {
      if (!validateImageFile(file)) {
        toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP, SVG)');
        return;
      }
    }

    try {
      setUploading(prev => ({ ...prev, [fileType]: true }));
      // Upload file to S3 (returns key/filename). Use helper for consistent public URL
      const s3Key = await uploadFile(file, file.name);
      const publicUrl = getS3PublicUrl(s3Key);

      // Update uploaded files state
      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: {
          file: file,
          url: publicUrl,
          key: s3Key,
          name: file.name
        }
      }));

      const typeLabelMap = {
        postcards: "Postcards",
        a1Posters: "A1 Posters",
        a2Posters: "A2 Posters",
        a3Posters: "A3 Posters",
        a4Posters: "A4 Posters",
        socialMediaBanners: "Social Media Banners",
        digitalFlyer: "Digital Flyer",
        qrCodes: "QR Codes",
        emailTemplates: "Email Templates",
        pullUpBanners: "Pull up Banners",
        emailSignatures: "Email Signatures",
      };
      const label = typeLabelMap[fileType] || "File";
      toast.success(`${label} uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleCardClick = (fileType) => {
    if (uploading[fileType]) return;
    const input = document.createElement('input');
    input.type = 'file';
    if (fileType === 'emailTemplates') {
      input.accept = '.html,.htm,.zip';
    } else if (fileType === 'qrCodes') {
      input.accept = '.png,.svg';
    } else {
      input.accept = 'image/*';
    }
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleUpload(fileType, file);
      }
    };
    input.click();
  };

  const OfferInfoContent = (
    <div className="assetform">
      <Input
        type="text"
        label="Title"
        placeholder="Enter Asset Title"
        width="100%"
        radius="5px"
        InputDivClass="w-[100%] mt-8"
        inputClass="px-4"
        labelClassName="lg:text-[14px] sm:text-[12px] font-[600] text-[#303030] mb-2 font-poppins"
        name="title"
        value={title}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.title}
        touched={touched.title}
      />
      <InputTextArea
        label={"Asset Description"}
        divClassName="relative mt-5 w-full"
        labelClassName="lg:text-[14px] sm:text-[12px] font-[600] text-[#303030] mb-2 font-poppins"
        textareaClassName="w-full h-[150px] px-4 pt-2 rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins"
        placeholder="Enter Asset Description"
        name="assetDescription"
        value={assetDescription}
        handleBlur={handleBlur}
        error={errors.assetDescription}
        touched={touched.assetDescription}
        setFieldValue={setFieldValue}
      />
      <Selector
        value={selectedFundraisers}
        defaultValue={
          id !== "" && id !== null ? selectedFundraisers : undefined
        }
        label={"Fundraisers"}
        labelClassName="lg:text-[14px] sm:text-[12px] font-[600] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
        divClassName="mt-4 w-full multipleField"
        selectClassName="w-full rounded-[5px] bg-[#FFFFFF] text-[#303030] lg:text-[16px] sm:text-[10px] font-[400] outline-none !font-poppins !focus:outline-none"
        flexClassName="gap-1"
        options={fundraiserOptions}
        suffixIcon={<DownArrow />}
        placeholder={"Select Fundraisers"}
        onChange={handleFundraisersChange}
        onDeselect={handleMerchantDeselect}
        handleBlur={handleBlur}
        error={errors.selectedFundraisers}
        touched={touched.selectedFundraisers}
        multiple={true}
        showSearch={true}
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    </div>
  );

  const MediaContent = (
    <>
      {/* Line divider */}
      <div className="w-full h-[1px] bg-[#E5E5E5] my-6"></div>

      <div className="mt-6 mb-4">
        <p className="text-[16px] font-[600] text-[#000000] font-poppins">
          Upload images
        </p>
      </div>

      {/* Custom Upload Section */}
      <div
        className="max-w-5xl mx-auto rounded-[20px] p-8"
        style={{
          border: "2px dashed #00ADEF",
          boxShadow: "0px 4px 20px 10px #7979791A",
        }}
      >
        {/* Responsive Grid: 1 col on mobile, 2 on tablet, 4 on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
          {/* Postcards */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.postcards ? 'border-green-400 bg-green-50' : 'border-[#00ADEF33] bg-[#F0FAFF33]'
                }`}
              onClick={() => handleCardClick('postcards')}
            >
              {uploading.postcards ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.postcards ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.postcards.url} alt={uploadedFiles.postcards.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.postcards.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">Postcards</p>
            <p className="text-[10px] text-gray-400">1500 × 1050 px</p>
          </div>

          {/* A1 Posters */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.a1Posters ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('a1Posters')}
            >
              {uploading.a1Posters ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.a1Posters ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.a1Posters.url} alt={uploadedFiles.a1Posters.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.a1Posters.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">A1 Posters</p>
            <p className="text-[10px] text-gray-400">5933 × 8408 px</p>
          </div>

          {/* A2 Posters */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.a2Posters ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('a2Posters')}
            >
              {uploading.a2Posters ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.a2Posters ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.a2Posters.url} alt={uploadedFiles.a2Posters.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.a2Posters.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">A2 Posters</p>
            <p className="text-[10px] text-gray-400">4200 × 5933 px</p>
          </div>

          {/* A3 Posters */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.a3Posters ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('a3Posters')}
            >
              {uploading.a3Posters ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.a3Posters ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.a3Posters.url} alt={uploadedFiles.a3Posters.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.a3Posters.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">A3 Posters</p>
            <p className="text-[10px] text-gray-400">3508 × 4961 px</p>
          </div>

          {/* A4 Posters */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.a4Posters ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('a4Posters')}
            >
              {uploading.a4Posters ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.a4Posters ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.a4Posters.url} alt={uploadedFiles.a4Posters.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.a4Posters.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">A4 Posters</p>
            <p className="text-[10px] text-gray-400">2480 × 3508 px</p>
          </div>

          {/* Social Media Banners */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.socialMediaBanners ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('socialMediaBanners')}
            >
              {uploading.socialMediaBanners ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.socialMediaBanners ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.socialMediaBanners.url} alt={uploadedFiles.socialMediaBanners.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.socialMediaBanners.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">Social Media Banners</p>
            <p className="text-[10px] text-gray-400">1080 × 1080 px</p>
          </div>

          {/* Digital Flyer (E Flyers) */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.digitalFlyer ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('digitalFlyer')}
            >
              {uploading.digitalFlyer ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.digitalFlyer ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.digitalFlyer.url} alt={uploadedFiles.digitalFlyer.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.digitalFlyer.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">Digital Flyer</p>
            <p className="text-[10px] text-gray-400">1920 × 1080 px</p>
          </div>

          {/* QR Codes */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.qrCodes ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('qrCodes')}
            >
              {uploading.qrCodes ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.qrCodes ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.qrCodes.url} alt={uploadedFiles.qrCodes.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.qrCodes.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">QR Codes</p>
            <p className="text-[10px] text-gray-400">PNG / SVG</p>
          </div>

          {/* Email Templates */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.emailTemplates ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('emailTemplates')}
            >
              {uploading.emailTemplates ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.emailTemplates ? (
                <>
                  <div className="w-full h-[56px] mb-1 overflow-hidden rounded bg-gray-50 flex items-center justify-center text-xs text-gray-600">
                    {uploadedFiles.emailTemplates.name}
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.emailTemplates.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload File</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">Email Templates</p>
            <p className="text-[10px] text-gray-400">HTML / ZIP</p>
          </div>

          {/* Pull up Banners */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.pullUpBanners ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('pullUpBanners')}
            >
              {uploading.pullUpBanners ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.pullUpBanners ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.pullUpBanners.url} alt={uploadedFiles.pullUpBanners.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.pullUpBanners.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">Pull up Banners</p>
            <p className="text-[10px] text-gray-400">850 × 2000 px</p>
          </div>

          {/* Email Signatures */}
          <div className="flex flex-col items-center">
            <div
              className={`w-full aspect-[2/1] bg-white border rounded-xl p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:border-[#00ADEF] hover:bg-[#F0FAFF] hover:shadow-lg ${uploadedFiles.emailSignatures ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
              onClick={() => handleCardClick('emailSignatures')}
            >
              {uploading.emailSignatures ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.emailSignatures ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.emailSignatures.url} alt={uploadedFiles.emailSignatures.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.emailSignatures.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-gray-400 mt-2">Email Signatures</p>
            <p className="text-[10px] text-gray-400">600 × 200 px</p>
          </div>
        </div>

        {/* Bottom text */}
        <div className="text-center mt-6">
          <p className="text-base text-gray-400">
            Drag & Drop or <span className="text-[#00ADEF] font-medium cursor-pointer">Browse</span> to Upload
          </p>
        </div>
      </div>
    </>
  );

  return (
    <div className="offerform">
      {loading === true && <Loader />}
      <BreadCrumsHeader
        heading="Add Marketing Assets"
        items={items}
        form={true}
      />
      {/* Render both sections: details and media */}
      <FormLayout title={"Add Marketing Assets"} content={<><div>{OfferInfoContent}</div><div className="mt-8">{MediaContent}</div></>} />

      <div className="col-span-12 text-end mt-14">
        <button
          className="w-[30%] lg:w-[15%] h-[45px] rounded-[5px] bg-[#F0F0F0] text-[16px] font-[500] text-[#8C8C8C]"
          style={{ border: "1px solid #C4C4C4" }}
          onClick={() => {
            navigate("/admin/promotions-list");
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className={`w-[30%] lg:w-[15%] h-[45px] rounded-[5px] text-[16px] font-[500] ms-2 transition-all ${!isFormValid()
            ? 'bg-[#E0E0E0] text-[#8C8C8C] cursor-not-allowed border border-[#C4C4C4]'
            : 'bg-[#00ADEF] text-[#FFFFFF]'
            }`}
          disabled={!isFormValid()}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default AddAssetsForm;


